import * as React from 'react';

import { RouteStore } from 'bernie-core';

import { AlternateDatesModel, HotelViewModel } from 'models';

import { UitkCard, UitkCardContent } from 'uitk-react-cards';

import { AdditionalInfo } from './additional-info';
import { AlternateDates } from './alternate-dates';
import { CardLink } from './card-link';
import { HighightedPropertyDetails } from './highlighted-property-details';
import { ListingCommonSection } from './listing-common-section';
import { ListingErrorBoundary } from './listing-error-boundary';
import { ListingFooter } from './footer';
import { ListingName } from './listing-name';
import { Neighborhood } from './neighborhood';
import { PlaceholderCard } from './placeholder-card';
import { PriceAndSupportingMessagesContainer } from './price-and-supporting-messages-container';
import { Stars } from './star-rating';
import { VipAndPropertyTypeBadge } from './vip-and-property-badge';

interface DefaultListingProps {
  className: string;
  index?: number;
  loading?: boolean;
  model: HotelViewModel;
  routeStore?: RouteStore;
  isDatelessPdp?: boolean;
  defaultListingTopMarginClass?: string;
  lowerRight?: string;
  stars?: number;
  alternateDatesProps?: {
    handleClick: () => void;
    model: AlternateDatesModel[];
  };
  getTitleComponent: (imageAspectRatio?: string, extraProps?: any) => any;
  alternateDatesInfo: {
    collapsedLinkName: string;
    collapsedMsgKey: string;
    collapsedRfrr: string;
    expandedLinkName: string;
    expandedMsgKey: string;
    expandedRfrr: string;
    headerTextKey: string;
  };
  dataStid?: string;
  cardLinkProps?: any;
  routeStoreGoBack?: () => void;
  listingClickHandler?: () => void;
  setItemRef?: () => void;
  excludePriceBreakDownLink?: boolean;
}

export const DefaultListing: React.FunctionComponent<DefaultListingProps> = ({
  alternateDatesProps,
  index,
  loading,
  model,
  routeStore,
  isDatelessPdp,
  className,
  defaultListingTopMarginClass,
  lowerRight,
  stars,
  getTitleComponent,
  alternateDatesInfo,
  dataStid,
  cardLinkProps,
  routeStoreGoBack,
  listingClickHandler,
  setItemRef,
  excludePriceBreakDownLink
}) => {
  const {
    alternateDates,
    listingFooter,
    name,
    neighborhood,
    price,
    propertySummary,
    secondaryCardMessage,
    starRatingIcon,
    supportingMessages,
    thumbnail,
    vipMessage,
    propertyTypeBadge, // Experiment: 32599 Property_Type_Badging
    recentlyViewed,
    priceBreakDownModel,
    infositeUrl
  } = model;
  const listingWrapperProps = {
    className,
    'data-stid': dataStid || '',
    ref: setItemRef,
    tabIndex: -1
  };

  const vipAndPropertyTypeBadgeProps = {
    vipMessage,
    propertyTypeBadge,
    recentlyViewed,
    isDatelessPdp
  };

  const listingNameProps = {
    name,
    ariaHidden: true,
    recentlyViewed
  };

  const highlightedPropertyDetailsProps = {
    propertyDetails: propertySummary
  };

  const priceAndSupportingMessagesContainerProps = {
    ...{ secondaryCardMessage, supportingMessages, price, priceBreakDownModel, infositeUrl, excludePriceBreakDownLink },
    wrapPrice: true,
    viewSmall: true,
    priceFontWeight: '600',
    hotelId: model.hotelId,
    recentlyViewed,
    isDatelessPdp
  };

  if (loading) {
    return (
      <li {...listingWrapperProps}>
        <PlaceholderCard index={index} />
      </li>
    );
  }

  return (
    <li {...listingWrapperProps}>
      <UitkCard className="has-action listing__card">
        <ListingName name={name} visuallyHidden={true} />
        <div className="link-container">
          <ListingErrorBoundary thumbnail={thumbnail} name={name}>
            {getTitleComponent()}
          </ListingErrorBoundary>
          <UitkCardContent className={`all-y-padding-three all-x-padding-three listing-content ${defaultListingTopMarginClass}`}>
            <>
              <VipAndPropertyTypeBadge {...vipAndPropertyTypeBadgeProps} />
              <div className="uitk-grid">
                <div className="uitk-cell all-cell-fill uitk-type-300">
                  <ListingName {...listingNameProps} lineCount={2} />
                </div>
              </div>
              <div className="uitk-grid all-cell-fill">
                <div className="uitk-cell all-cell-1-2 uitk-type-300">
                  <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                  <Neighborhood neighborhood={neighborhood} />
                </div>
              </div>
              <div className="uitk-grid">
                <div className="uitk-cell all-cell-fill all-cell-align-bottom uitk-type-300">
                  <Stars stars={stars} starRatingIcon={starRatingIcon} />
                  <ListingCommonSection model={model} routeStore={routeStore} routeStoreGoBack={routeStoreGoBack} />
                </div>
                <div className="uitk-cell all-cell-fill uitk-type-right all-cell-align-bottom">
                  <PriceAndSupportingMessagesContainer {...priceAndSupportingMessagesContainerProps} />
                  {lowerRight && !isDatelessPdp && <div className="uitk-cell all-cell-shrink all-cell-align-bottom">{lowerRight}</div>}
                </div>
              </div>
            </>
          </UitkCardContent>
          <CardLink {...cardLinkProps} index={index} listingClickHandler={listingClickHandler} />
        </div>
        <ListingFooter {...cardLinkProps} listingFooter={listingFooter} listingClickHandler={listingClickHandler} />
        {alternateDates && (
          <AdditionalInfo {...alternateDatesInfo} displayHeaderText={false} keepOpen={false}>
            <AlternateDates {...alternateDatesProps} />
          </AdditionalInfo>
        )}
      </UitkCard>
    </li>
  );
};
